import { useState, useEffect } from "react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Spinner } from "../ui/spinner";
import { InstallflowFrame } from "./install-flow-frame";
import ThaloLogo from "@/assets/thalo-light.png";
import { checkEmailExists } from "@/api/existEmail";
import { login } from "@/auth";
import { useNavigate } from "@tanstack/react-router";
import {
  installFlowSensorKitSerial,
  installFlowEmail,
} from "@/installFlow/storage-keys";

// ─── Schemas ──────────────────────────────────────────────────────────────────

const emailSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
});

const passwordSchema = z.object({
  password: z.string().nonempty({ message: "Password can't be empty" }),
});

interface AuthProps extends React.HTMLAttributes<HTMLDivElement> {}

// ─── Component ────────────────────────────────────────────────────────────────

export function LoginInstallflow({ className }: AuthProps) {
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [serialNumber, setSerialNumber] = useState<string>("");

  // "email" = step 1, "password" = step 2
  const [step, setStep] = useState<"email" | "password">("email");
  // Stored after email is confirmed to exist
  const [confirmedEmail, setConfirmedEmail] = useState<string>("");

  // Controls the slide-out / slide-in animation
  const [animating, setAnimating] = useState(false);
  // Which direction is the new content entering from
  const [enterFrom, setEnterFrom] = useState<"right" | "left">("right");

  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    const stored = installFlowSensorKitSerial.get();
    if (stored) setSerialNumber(stored);
  }, []);

  // ── Forms ────────────────────────────────────────────────────────────────

  const emailForm = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
    mode: "onChange",
  });

  const passwordForm = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: "" },
    mode: "onChange",
  });

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** Slide the current content out, swap step state, then slide new content in */
  function transitionTo(nextStep: "email" | "password", direction: "right" | "left") {
    setEnterFrom(direction);
    setAnimating(true);
    // after exit animation completes, swap content and trigger enter
    setTimeout(() => {
      setStep(nextStep);
      setAnimating(false);
    }, 300);
  }

  // ── Step 1 – Email submit ─────────────────────────────────────────────────

  async function onEmailSubmit(values: z.infer<typeof emailSchema>) {
    setEmailError("");
    setIsCheckingEmail(true);
    try {
      await checkEmailExists(values.email);
      // Save email so password step can use it
      setConfirmedEmail(values.email);
      installFlowEmail.set(values.email);
      transitionTo("password", "right");
    } catch {
      setEmailError("This email is not registered. Please check and try again.");
    } finally {
      setIsCheckingEmail(false);
    }
  }

  // ── Step 2 – Password submit ──────────────────────────────────────────────

  async function onPasswordSubmit(values: z.infer<typeof passwordSchema>) {
    setPasswordError("");
    setIsLoggingIn(true);
    try {
      await login(confirmedEmail, values.password);
      window.location.href = "/installflow/select-organization";
    } catch (error: any) {
      setPasswordError(
        error?.response?.data?.non_field_errors?.[0] ??
          error?.response?.data?.detail ??
          "Incorrect password. Please try again."
      );
    } finally {
      setIsLoggingIn(false);
    }
  }

  // ── Forgot password ───────────────────────────────────────────────────────

  function handleForgotPassword() {
    navigate({ to: "/installFlow/forgot-password" });
  }

  // ── Shared header badge ───────────────────────────────────────────────────

  const HeaderBadge = (
    <Button
      type="button"
      variant="thalo"
      className="w-[70%] h-[50px] p-4 flex items-center justify-center"
    >
      <img src={ThaloLogo} className="w-1/2" alt="Logo" />
      <div className="h-full w-[1px] bg-border mx-2" />
      <div className="opacity-[50%] text-base font-semibold">{serialNumber}</div>
    </Button>
  );

  // ── Animation classes ─────────────────────────────────────────────────────

  const slideOut =
    enterFrom === "right"
      ? "-translate-x-8 opacity-0"
      : "translate-x-8 opacity-0";

  const contentClass = cn(
    "transition-all duration-300 ease-in-out",
    animating ? slideOut : "translate-x-0 opacity-100"
  );

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <InstallflowFrame>
      <div className="w-full overflow-hidden">
        <div className={contentClass}>
          {/* ── STEP 1: Email ── */}
          {step === "email" && (
            <Form {...emailForm}>
              <form
                onSubmit={emailForm.handleSubmit(onEmailSubmit)}
                className="space-y-4"
              >
                <div className="grid gap-6">
                  <div className="grid gap-6">
                    {HeaderBadge}
                    <FormField
                      control={emailForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="gap-4 flex flex-col">
                          <h2 className="text-xl font-bold text-black leading-3">
                            Please enter your work email
                          </h2>
                          <label
                            className="text-[16px] font-semibold text-md text-gray-600 !mt-0"
                            htmlFor="email"
                          >
                            We just need to make sure you're authorized to
                            install this device.
                          </label>
                          <FormControl>
                            <Input
                              placeholder="Email"
                              className="h-12 !mt-0"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                          {emailError && (
                            <p className="text-sm font-medium text-destructive">
                              {emailError}
                            </p>
                          )}
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      variant="installFlow"
                      type="submit"
                      disabled={
                        !emailForm.formState.isValid || isCheckingEmail
                      }
                    >
                      {isCheckingEmail ? (
                        <Spinner className="h-6 w-6" />
                      ) : (
                        "Continue"
                      )}
                    </Button>
                  </div>

                  <p
                    className={cn(
                      "text-sm font-medium text-destructive",
                      className
                    )}
                  >
                    {emailForm.formState.errors.root?.message}
                  </p>
                </div>
              </form>
            </Form>
          )}

          {/* ── STEP 2: Password ── */}
          {step === "password" && (
            <Form {...passwordForm}>
              <form
                onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
                className="space-y-4"
              >
                <div className="grid gap-6">
                  <div className="grid gap-6">
                    <FormField
                      control={passwordForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem className="gap-4 flex flex-col">
                          <h2 className="text-xl font-bold text-black leading-3">
                            Enter your password
                          </h2>
                          <FormControl>
                            <Input
                              id="password"
                              placeholder="Password"
                              type="password"
                              className="h-12 !mt-0"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                          {passwordError && (
                            <p className="text-sm font-medium text-destructive">
                              {passwordError}
                            </p>
                          )}
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      variant="installFlow"
                      type="submit"
                      disabled={
                        !passwordForm.formState.isValid || isLoggingIn
                      }
                    >
                      {isLoggingIn ? (
                        <Spinner className="h-6 w-6" />
                      ) : (
                        "Continue"
                      )}
                    </Button>

                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="text-sm underline text-gray-600 hover:text-gray-900 text-center mt-1 bg-transparent border-none cursor-pointer"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <p
                    className={cn(
                      "text-sm font-medium text-destructive",
                      className
                    )}
                  >
                    {passwordForm.formState.errors.root?.message}
                  </p>
                </div>
              </form>
            </Form>
          )}
        </div>
      </div>
    </InstallflowFrame>
  );
}
